<template>
    <section v-if="showPaymentsHeader" class="invoice-payments">
        <div class="title-container desktop">
            <h6 class="bold">
                {{ $t('InvoicePayments.title', { id: sequentialInvoiceEnabled ? invoice.invoiceNumber : invoice.id}) }}
            </h6>
        </div>

        <div class="title-container mobile">
            <h3>{{ $t('InvoicePayments.mobileTitle') }}</h3>
        </div>

        <DsTableGrid
            v-if="Boolean(sanitizedPayments.length)"
            show-loading
            data-qa="payment-table"
            :data="sanitizedPayments"
        >
            <DsTableGridColumn
                prop="date"
                :label="$t('InvoicePayments.date')"
            >
                <template #default="props">
                    {{ props.date }}
                </template>
            </DsTableGridColumn>

            <DsTableGridColumn
                prop="sortName"
                :label="$t('InvoicePayments.type')"
            >
                <template #default="props">
                    <span :class="{ 'adjusted': props.isAdjusted }">
                        {{ props.typeText }}
                    </span>
                </template>
            </DsTableGridColumn>

            <DsTableGridColumn
                prop="sortName"
                :label="$t('InvoicePayments.amount')"
            >
                <template #default="props">
                    <span :class="{ 'adjusted': props.isAdjusted }">
                        {{ adjustedAmount(props.isAdjusted, props.amount) }}
                    </span>
                </template>
            </DsTableGridColumn>
        </DsTableGrid>
    </section>
</template>

<script>
import { DsTableGrid, DsTableGridColumn } from '@infusionsoft/design-system';
import { getPaymentType } from '../../money.util';
import { formatDate, DISPLAY_DATE_FORMAT_WITH_YEAR } from '@keap-web/shared-ui';

export default {
    components: {
        DsTableGrid,
        DsTableGridColumn,
    },

    props: {
        companyProfile: Object,
        invoice: {
            type: Object,
            default: () => {
                return {
                    id: '',
                    contact: {
                        firstName: '',
                    },
                };
            },
        },
    },

    data() {
        return {
            rows: [{
                height: '2rem',
                boxes: [1],
            }],
        };
    },

    computed: {
        sequentialInvoiceEnabled() {
            return this.invoice?.featureFlags?.sequentialInvoiceId;
        },

        hasPayments() {
            return Boolean(this.payments && this.payments.length);
        },

        payments() {
            return this.invoice.paymentsMade || [];
        },

        showPaymentsHeader() {
            return Boolean(this.invoice.id
            && this.invoice.total > 0
            && this.hasPayments);
        },

        sanitizedPayments() {
            return this.payments.map((payment) => {
                return {
                    ...payment,
                    date: formatDate(payment.payDate, DISPLAY_DATE_FORMAT_WITH_YEAR),
                    typeText: this.$t(getPaymentType(payment.payStatus)),
                    isAdjusted: this.isAdjusted(payment.amount, payment.payStatus),
                };
            });
        },

        isRecurring() {
            return Boolean(this.invoice?.orderItems?.some(({ recurringBilling }) => recurringBilling));
        },
    },

    methods: {
        isAdjusted(amount, payStatus) {
            return amount < 0 && payStatus.toLowerCase().includes('adjustment');
        },

        adjustedAmount(isAdjusted, amount) {
            if (isAdjusted) {
                amount = Math.abs(amount);

                return this.$n(amount, 'currency');
            }

            return this.$n(amount, 'currency');
        },
    },
};
</script>

<style lang="scss">
    @import '../../styles/common';

    .table-grid {
        tr:last-child {
            border-bottom: 0;
        }
    }
</style>

<style lang="scss" scoped>
    @import '../../styles/common';

    section {
        border-top: 1px solid $color-gray-200;
    }

    .icon-info-circle {
        margin-left: $spacing-200;
        color: $color-gray-700;
    }

    .refund-text {
        margin-left: $spacing-200;
    }

    .invoice-refund-info {
        background-color: $color-gray-100;
        height: 3.4rem;
        display: flex;
        align-items: center;
        border-radius: 0.75rem;
    }

    .title-with-refund {
        display: flex;
    }

    .file-header {
        padding-top: $spacing-100;
        width: 100%;
    }

    .file-empty-state {
        size: $font-size-md;
        color: $color-gray-700;
    }

    .title-container {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        flex-direction: column;
        padding: $spacing-300 $spacing-500;
    }

    .mobile {
        display: none;
        --icon-color: #{$color-blue};
    }

    .table-grid {
        padding: 0 $spacing-500;
        margin-bottom: $spacing-400;
    }

    .adjusted {
        color: $color-red;
    }

    @media print {
        .add-payment-button {
            display: none;
        }
    }

    @media($small) {
        .table-grid {
            padding: 0;
        }

        .title-container {
            padding: $spacing-200;
        }

        .desktop {
            display: none;
        }

        .mobile {
            display: flex;
        }
    }
</style>
